/** Spark area chart for compact time-series. Uses {@link getChartColorForSvg} and palette from chartUtils. */

import { forwardRef, useId } from "react";
import {
  Area,
  AreaChart as RechartsAreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { cn } from "@/lib/utils";
import {
  AvailableChartColors,
  type AvailableChartColorsKeys,
  constructCategoryColors,
  getChartColorForSvg,
  getYAxisDomain,
  getYAxisDomainForSeriesData,
} from "@/lib/utils/chartUtils";
import type { SparkAreaChartProps } from "./SparkChart.types";

/** Stable hash for a string (avoids gradient id collision e.g. "S&P 500" vs "S P 500"). */
const hashString = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
};

/** Safe, unique id for SVG gradient (url(#id) can break when useId() contains colons). */
const safeGradientId = (rawId: string, category: string) =>
  `spark-${rawId.replace(/\W/g, "")}-${hashString(category)}`;

const getFillContent = (fill: SparkAreaChartProps["fill"], color: string) => {
  switch (fill) {
    case "none":
      return <stop stopColor={color} stopOpacity={0} />;
    case "gradient":
      return (
        <>
          <stop offset="5%" stopColor={color} stopOpacity={0.4} />
          <stop offset="95%" stopColor={color} stopOpacity={0} />
        </>
      );
    case "solid":
      return <stop stopColor={color} stopOpacity={0.3} />;
    default:
      return <stop stopColor={color} stopOpacity={0.3} />;
  }
};

export const SparkAreaChart = forwardRef<HTMLDivElement, SparkAreaChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      connectNulls = false,
      type = "default",
      className,
      fill = "gradient",
      width: widthProp,
      height: heightProp,
      ...other
    } = props;

    const categoryColors = constructCategoryColors(categories, colors);
    const yAxisDomain =
      minValue != null || maxValue != null
        ? getYAxisDomain(autoMinValue, minValue, maxValue)
        : getYAxisDomainForSeriesData(data, categories, { autoMinValue });
    const stacked = type === "stacked" || type === "percent";
    const areaId = useId();
    const useFixedSize =
      widthProp != null &&
      heightProp != null &&
      widthProp > 0 &&
      heightProp > 0;

    const chartContent = (
      <RechartsAreaChart
        width={useFixedSize ? widthProp : undefined}
        height={useFixedSize ? heightProp : undefined}
        data={data}
        margin={{
          bottom: 1,
          left: 1,
          right: 1,
          top: 1,
        }}
        stackOffset={type === "percent" ? "expand" : undefined}
      >
        <defs>
          {categories.map((category) => {
            const colorKey = categoryColors.get(category) as
              | AvailableChartColorsKeys
              | undefined;
            const categoryId = safeGradientId(areaId, category);
            const color = getChartColorForSvg(colorKey ?? "gray");
            return (
              <linearGradient
                key={category}
                id={categoryId}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                {getFillContent(fill, color)}
              </linearGradient>
            );
          })}
        </defs>
        <XAxis hide dataKey={index} />
        <YAxis hide domain={yAxisDomain as AxisDomain} />
        {categories.map((category) => {
          const colorKey = categoryColors.get(category) as
            | AvailableChartColorsKeys
            | undefined;
          const categoryId = safeGradientId(areaId, category);
          const color = getChartColorForSvg(colorKey ?? "gray");
          return (
            <Area
              key={category}
              dot={false}
              stroke={color}
              strokeOpacity={1}
              name={category}
              type="linear"
              dataKey={category}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              isAnimationActive={false}
              connectNulls={connectNulls}
              stackId={stacked ? "stack" : undefined}
              fill={`url(#${categoryId})`}
            />
          );
        })}
      </RechartsAreaChart>
    );

    return (
      <div
        ref={forwardedRef}
        aria-hidden
        className={cn("h-12 w-28 min-h-10 min-w-28", className)}
        style={
          useFixedSize
            ? { width: widthProp, height: heightProp }
            : { width: "100%", height: "100%" }
        }
        {...other}
      >
        {useFixedSize ? (
          chartContent
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartContent}
          </ResponsiveContainer>
        )}
      </div>
    );
  },
);

SparkAreaChart.displayName = "SparkAreaChart";
